import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import BulkSendShell from '../components/BulkSendShell';
import CampaignFields from '../components/CampaignFields';
import CampaignSentPanel from '../components/CampaignSentPanel';
import ConfirmSendModal from '../components/ConfirmSendModal';
import NoticePanel from '../components/NoticePanel';
import RecipientsSection from '../components/RecipientsSection';
import TestSendBlock from '../components/TestSendBlock';
import WhatsappMessagePreview from '../components/WhatsappMessagePreview';
import { useCampaignSend } from '../hooks/useCampaignSend';
import { useCsvSheet } from '../hooks/useCsvSheet';
import { useObjectUrl } from '../hooks/useObjectUrl';
import { useWhatsappTemplates } from '../hooks/useWhatsappTemplates';
import type { CampaignFormValues } from '../types/whatsapp';
import { buildRecipientsFile } from '../utils/buildRecipientsFile';

export default function WhatsappBulkSend() {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<CampaignFormValues>({ shouldUnregister: true });

  const [mediaFile, setMediaFile] = useState<File | null>(null);
  const [sampleValues, setSampleValues] = useState<Record<string, string>>({});
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const mediaUrl = useObjectUrl(mediaFile);
  const sheet = useCsvSheet();
  const campaign = useCampaignSend();

  const templatesQuery = useWhatsappTemplates();

  const templates = templatesQuery.data ?? [];
  const selectedId = watch('template_meta_id');
  const template = templates.find((one) => one.meta_id === selectedId) ?? null;
  const variableNumbers = Array.from(
    { length: template?.body_variable_count ?? 0 },
    (_, index) => index + 1,
  );

  useEffect(() => {
    sheet.clear();
    setMediaFile(null);
    setSampleValues({});
    campaign.clear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const reviewCampaign = () => {
    if (!sheet.file || !sheet.parsed) {
      toast.error('Add the recipients CSV first.');
      return;
    }

    sheet.validate(variableNumbers);
    setIsConfirmOpen(true);
  };

  const confirmSend = async () => {
    if (!template || !sheet.file || !sheet.check) return;

    const { rowsToSend } = sheet.check;
    await campaign.send({
      template,
      recipientsFile: buildRecipientsFile(
        rowsToSend,
        variableNumbers,
        sheet.file.name,
      ),
      recipientCount: rowsToSend.length,
      mediaFile,
      values: watch(),
    });
    setIsConfirmOpen(false);
  };

  const startAnother = () => {
    reset();
    sheet.clear();
    setMediaFile(null);
    setSampleValues({});
    campaign.clear();
  };

  if (templatesQuery.isLoading) {
    return (
      <BulkSendShell>
        <div className="text-gray-500">Loading...</div>
      </BulkSendShell>
    );
  }

  if (templatesQuery.error) {
    return (
      <BulkSendShell>
        <NoticePanel
          title="Could not load the templates"
          actionLabel="Try again"
          onAction={() => templatesQuery.refetch()}
        >
          The template list comes from Meta each time this page opens.
        </NoticePanel>
      </BulkSendShell>
    );
  }

  if (campaign.sent) {
    return (
      <BulkSendShell>
        <div className="max-w-2xl">
          <CampaignSentPanel {...campaign.sent} onStartAnother={startAnother} />
        </div>
      </BulkSendShell>
    );
  }

  if (templates.length === 0) {
    return (
      <BulkSendShell>
        <NoticePanel title="No approved templates yet">
          Templates are written and approved in Meta. Once Meta approves one it
          shows up here on its own.
        </NoticePanel>
      </BulkSendShell>
    );
  }

  return (
    <BulkSendShell>
      <div className="grid gap-8 lg:grid-cols-3">
        <form
          onSubmit={handleSubmit(reviewCampaign)}
          className="space-y-6 lg:col-span-2"
        >
          <CampaignFields
            templates={templates}
            template={template}
            register={register}
            errors={errors}
            mediaFile={mediaFile}
            onMediaChange={setMediaFile}
          />

          {template && (
            <TestSendBlock
              template={template}
              variableNumbers={variableNumbers}
              mediaFile={mediaFile}
              buttonSuffix={watch('button_suffix')}
              couponCode={watch('coupon_code')}
              disabled={campaign.isSending}
              values={sampleValues}
              onValueChange={(number, value) =>
                setSampleValues((current) => ({
                  ...current,
                  [String(number)]: value,
                }))
              }
            />
          )}

          <RecipientsSection
            template={template}
            file={sheet.file}
            isParsing={sheet.isParsing}
            problems={sheet.check?.sheetProblems ?? []}
            showProblems={!isConfirmOpen}
            strandedCampaignId={campaign.strandedCampaignId}
            onFileSelected={sheet.select}
            onFileCleared={sheet.clear}
          />

          <button
            type="submit"
            disabled={campaign.isSending || sheet.isParsing || !sheet.file}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Send comms
          </button>
        </form>

        <div className="lg:col-span-1">
          <div className="sticky top-0">
            <WhatsappMessagePreview
              template={template}
              values={sampleValues}
              mediaUrl={mediaUrl}
            />
          </div>
        </div>
      </div>

      <ConfirmSendModal
        isOpen={isConfirmOpen}
        isSending={campaign.isSending}
        uploadPercent={campaign.uploadPercent}
        campaignName={watch('campaign_name') ?? ''}
        sheetProblems={sheet.check?.sheetProblems ?? []}
        rowsToSend={sheet.check?.rowsToSend ?? []}
        rowsToSkip={sheet.check?.rowsToSkip ?? []}
        variableNumbers={variableNumbers}
        template={template}
        mediaUrl={mediaUrl}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={confirmSend}
      />
    </BulkSendShell>
  );
}
